import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueries } from '@tanstack/react-query';
import { getQrCodeSettings, getSettings } from '~/services/queries/line';
import { getUnit } from '~/services/queries/unit';
import type { QrCodeSettingsProps } from '~/services/queries/line/useQrCodeSettings';

type UseLineControllerReturn = {
  loading: boolean;
  error: unknown;
  unitData: any | null;
  qrCodeSettingsData: QrCodeSettingsProps | null;
};

export function useLineController(): UseLineControllerReturn {
  const router = useRouter();
  const unitId = useMemo(
    () => (router.query.unitId ? String(router.query.unitId) : ''),
    [router.query.unitId]
  );

  const isBootstrapping = !router.isReady || !unitId;

  const unitQuery = useQuery({
    queryKey: ['unit', unitId],
    enabled: !!unitId,
    retry: false,
    queryFn: async () => {
      const res = await getUnit({ unit_id: unitId });
      if (res.status !== 200) {
        const err: any = new Error('Unit error');
        err.status = res.status;
        throw err;
      }
      return res.data;
    },
    staleTime: 60_000,
  });

  const qrCodeSettingsQuery = useQuery({
    queryKey: ['qrCodeSettings', unitId],
    enabled: !!unitId,
    retry: false,
    queryFn: async () => {
      const res = await getQrCodeSettings({ unit_id: unitId });
      if (res.status !== 200) {
        const err: any = new Error('QR Code Settings error');
        err.status = res.status;
        throw err;
      }
      return res.data;
    },
    staleTime: 60_000,
  });

  const loading =
    isBootstrapping || unitQuery.isLoading || qrCodeSettingsQuery.isLoading;

  const error = unitQuery.error || qrCodeSettingsQuery.error || null;

  return {
    loading,
    error,
    unitData: unitQuery.data ?? null,
    qrCodeSettingsData: qrCodeSettingsQuery.data ?? null,
  };
}
